import { memo } from 'react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { baseInputClasses, getSelectClasses } from '../../styles/inputStyles'

/**
 * Reusable admin table controls component
 * Provides search, filter, and pagination controls for admin tables
 * 
 * @param {Object} props
 * @param {string} props.searchValue - Current search input value
 * @param {Function} props.onSearchChange - Search input change handler
 * @param {string} props.searchPlaceholder - Placeholder text for search input
 * @param {string} props.filterValue - Current filter value
 * @param {Function} props.onFilterChange - Filter change handler
 * @param {Array} props.filterOptions - Array of {value, label} filter options
 * @param {number} props.currentPage - Current pagination page
 * @param {number} props.totalPages - Total number of pages
 * @param {Function} props.onPageChange - Page change handler
 * @param {number} props.totalItems - Total number of items
 * @param {string} props.itemLabel - Label for items (e.g., "users", "entities")
 */
const AdminTableControls = memo(function AdminTableControls({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  filterValue,
  onFilterChange,
  filterOptions = [],
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemLabel = 'items'
}) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4">
      {/* Search and Filter Row */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search Input */}
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className={`${baseInputClasses} pl-10`}
          />
        </div>

        {/* Filter Dropdown */}
        {filterOptions.length > 0 && (
          <div className="sm:w-48">
            <select
              value={filterValue}
              onChange={(e) => onFilterChange(e.target.value)}
              className={getSelectClasses()}
            >
              {filterOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t pt-4">
          <div className="text-sm text-gray-600">
            Showing page {currentPage} of {totalPages} ({totalItems} {itemLabel})
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            {/* Page Numbers */}
            <div className="hidden sm:flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = currentPage - 2 + i
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                      currentPage === pageNum
                        ? 'bg-primary text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>
            
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
})

export default AdminTableControls
